import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EventApi, EndeavorApi } from '../../services/ApiService';
import { useData } from '../../contexts/DataContext';
import './MobileEventEditPage.css';

function MobileEventEditPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { clearCache, updateEvent, getEventTypes } = useData();
  
  const [event, setEvent] = useState(null);
  const [endeavor, setEndeavor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    eventType: 'lecture',
    startDatetime: '',
    endDatetime: '',
    location: ''
  });

  const [serverEventTypes, setServerEventTypes] = useState([]);

  // Fetch event data
  const fetchEventData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('MobileEventEditPage: Fetching event data for ID:', eventId);

      // Fetch event details
      const eventResponse = await EventApi.getEvent(eventId);
      if (!eventResponse.ok) {
        throw new Error(`Failed to fetch event: ${eventResponse.status}`);
      }

      const eventData = await eventResponse.json();
      console.log('MobileEventEditPage: Event data:', eventData);
      setEvent(eventData);

      // Update form data
      setFormData({
        name: eventData.name || eventData.title || '',
        description: eventData.description || '',
        eventTypeId: eventData.eventTypeId || eventData.eventType_id || null,
        eventType: eventData.eventType || 'lecture',
        startDatetime: eventData.startDatetime ? 
          new Date(eventData.startDatetime).toISOString().slice(0, 16) : '',
        endDatetime: eventData.endDatetime ? 
          new Date(eventData.endDatetime).toISOString().slice(0, 16) : '',
        location: eventData.location || ''
      });

      // Fetch endeavor data if event has endeavorId
      if (eventData.endeavorId) {
        try {
          const endeavorResponse = await EndeavorApi.getEndeavor(eventData.endeavorId);
          if (endeavorResponse.ok) {
            const endeavorData = await endeavorResponse.json();
            console.log('MobileEventEditPage: Endeavor data:', endeavorData);
            setEndeavor(endeavorData);
          }
        } catch (endeavorError) {
          console.warn('MobileEventEditPage: Error fetching endeavor:', endeavorError);
        }
      }

    } catch (error) {
      console.error('MobileEventEditPage: Error fetching event data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      fetchEventData();
    }
    (async () => {
      try {
        const types = await getEventTypes();
        const list = Array.isArray(types) ? types : (types.content || []);
        setServerEventTypes(list);
        console.log('MobileEventEditPage: loaded server event types:', list);
      } catch (e) { console.warn('MobileEventEditPage: failed to load event types', e); }
    })();
  }, [eventId, fetchEventData, getEventTypes]);

  // Handle form input changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'eventTypeId' ? (value ? Number(value) : null) : value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  }, [error]);

  // Form validation
  const validationErrors = useMemo(() => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Event name is required';
    }
    
    if (!formData.startDatetime) {
      errors.startDatetime = 'Start date and time is required';
    }
    
    if (formData.startDatetime && formData.endDatetime) {
      const startDate = new Date(formData.startDatetime);
      const endDate = new Date(formData.endDatetime);
      if (endDate <= startDate) {
        errors.endDatetime = 'End time must be after start time';
      }
    }
    
    return errors;
  }, [formData]);

  const isFormValid = useMemo(() => {
    return Object.keys(validationErrors).length === 0;
  }, [validationErrors]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!isFormValid) {
      setError('Please fix the validation errors below');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Helper to convert input (datetime-local) into a naive server datetime string without timezone
      const toServerDateTime = (val) => {
        if (!val) return null;
        const m = val.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?$/);
        if (m) return `${m[1]}:00`;
        try {
          const d = new Date(val);
          const pad = (n) => String(n).padStart(2, '0');
          return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
        } catch (e) {
          return null;
        }
      };

      const updateData = {
        eventId: Number(eventId),
        name: formData.name.trim(),
        description: formData.description.trim(),
        eventTypeId: (typeof formData.eventTypeId !== 'undefined' && formData.eventTypeId !== null) ? formData.eventTypeId : ((event && (event.eventTypeId || event.eventType_id || event.event_type_id)) || null),
        eventType: formData.eventType,
        startDatetime: toServerDateTime(formData.startDatetime),
        endDatetime: toServerDateTime(formData.endDatetime),
        location: formData.location.trim(),
        endeavorId: event?.endeavorId,
        isComplete: typeof event?.isComplete !== 'undefined' ? event.isComplete : (typeof event?.completed !== 'undefined' ? event.completed : false),
        isRecurring: event?.isRecurring || false,
        recurrencePattern: event?.recurrencePattern || null
      };

      // Sanitize payload
      const sanitized = Object.keys(updateData).reduce((acc, key) => {
        const val = updateData[key];
        if (val !== null && typeof val !== 'undefined') acc[key] = val;
        return acc;
      }, {});

      try { console.log('MobileEventEditPage: Outbound (sanitized) JSON:', JSON.stringify(sanitized)); } catch (err) { /* ignore */ }

      const changedFields = Object.keys(sanitized).reduce((acc, key) => {
        try {
          const before = event && event[key];
          const after = sanitized[key];
          if (String(before) !== String(after)) acc[key] = { before, after };
        } catch (e) { }
        return acc;
      }, {});
      console.log('MobileEventEditPage: Fields changed:', changedFields);

      const response = await updateEvent(eventId, sanitized);
      if (!response || !response.ok) {
        let errorText = '<no-response-body>';
        try { errorText = response && response.text ? await response.text() : errorText; } catch (er) { /* ignore */ }
        console.error('MobileEventEditPage: updateEvent failed', { status: response && response.status, body: errorText });
        throw new Error(`Failed to update event: ${response && response.status} - ${errorText}`);
      }

      try {
        const responseBody = await response.json();
        console.log('MobileEventEditPage: updateEvent response body:', responseBody);
      } catch (e) {
        console.log('MobileEventEditPage: updateEvent returned no JSON body');
      }

      console.log('MobileEventEditPage: Event updated successfully');

      clearCache('event');
      clearCache('user-endeavors');
      navigate(`/m/event/${eventId}`);

    } catch (error) {
      console.error('MobileEventEditPage: Error updating event:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  }, [eventId, formData, event, navigate, clearCache, isFormValid, updateEvent]);

  // Get event type data
  const getCurrentEventType = () => {
    return serverEventTypes.find(type => {
      const candidateId = Number(type.eventTypeId || type.id || type.event_type_id);
      return candidateId === Number(formData.eventTypeId);
    }) || { icon: '📅', name: formData.eventType || 'Event' };
  };

  if (loading) {
    return (
      <div className="mobile-event-edit">
        <div className="mobile-loading">
          <div className="loading-spinner"></div>
          <p>Loading event...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="mobile-event-edit">
        <div className="mobile-error">
          <div className="error-icon">📅</div>
          <h2>Event Not Found</h2>
          <p>The event you're trying to edit doesn't exist.</p>
          <button 
            className="mobile-btn mobile-btn-secondary"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const currentEventType = getCurrentEventType();

  return (
    <div className="mobile-event-edit">
      {/* Mobile Header */}
      <div className="mobile-edit-header">
        <button 
          className="mobile-back-btn"
          onClick={() => navigate(`/m/event/${eventId}`)}
        >
          ← Back
        </button>
        <div className="mobile-header-content">
          <div className="mobile-event-icon">
            {currentEventType.icon}
          </div>
          <div className="mobile-header-text">
            <h1>Edit Event</h1>
            {endeavor && (
              <span className="mobile-endeavor-tag">{endeavor.name}</span>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Form */}
      <div className="mobile-form-container">
        {error && (
          <div className="mobile-error-alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <form className="mobile-form">
          {/* Event Information Section */}
          <div className="mobile-form-section">
            <h3 className="mobile-section-title">
              <span className="section-icon">📅</span>
              Event Information
            </h3>

            <div className="mobile-form-group">
              <label className="mobile-label">
                Event Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter event name"
                className={`mobile-input ${validationErrors.name ? 'error' : ''}`}
              />
              {validationErrors.name && (
                <span className="mobile-error-text">{validationErrors.name}</span>
              )}
            </div>

            <div className="mobile-form-group">
              <label className="mobile-label">Event Type</label>
              <select
                name="eventTypeId"
                value={formData.eventTypeId || ''}
                onChange={handleInputChange}
                className="mobile-select"
              >
                <option value="">Select an event type...</option>
                {serverEventTypes.map(type => (
                  <option key={type.eventTypeId || type.id || type.event_type_id} value={type.eventTypeId || type.id || type.event_type_id}>
                    { (type.icon ? type.icon + ' ' : '') + (type.name || type.label || type.eventTypeName || type.event_type_name) }
                  </option>
                ))}
              </select>
            </div>

            <div className="mobile-form-group">
              <label className="mobile-label">Location</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Enter location"
                className="mobile-input"
              />
            </div>
          </div>

          {/* Date & Time Section */}
          <div className="mobile-form-section">
            <h3 className="mobile-section-title">
              <span className="section-icon">🕒</span>
              Date & Time
            </h3>

            <div className="mobile-form-group">
              <label className="mobile-label">
                Start Date & Time <span className="required">*</span>
              </label>
              <input
                type="datetime-local"
                name="startDatetime"
                value={formData.startDatetime}
                onChange={handleInputChange}
                className={`mobile-input ${validationErrors.startDatetime ? 'error' : ''}`}
              />
              {validationErrors.startDatetime && (
                <span className="mobile-error-text">{validationErrors.startDatetime}</span>
              )}
            </div>

            <div className="mobile-form-group">
              <label className="mobile-label">End Date & Time</label>
              <input
                type="datetime-local"
                name="endDatetime"
                value={formData.endDatetime}
                onChange={handleInputChange}
                className={`mobile-input ${validationErrors.endDatetime ? 'error' : ''}`}
              />
              {validationErrors.endDatetime && (
                <span className="mobile-error-text">{validationErrors.endDatetime}</span>
              )}
            </div>
          </div>

          {/* Description Section */}
          <div className="mobile-form-section">
            <h3 className="mobile-section-title">
              <span className="section-icon">📝</span>
              Description
            </h3>

            <div className="mobile-form-group">
              <label className="mobile-label">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter event description, instructions, or requirements..."
                className="mobile-textarea"
                rows={4}
              />
            </div>
          </div>
        </form>

        {/* Mobile Actions */}
        <div className="mobile-actions">
          <button 
            type="button"
            className="mobile-btn mobile-btn-secondary"
            onClick={() => navigate(`/m/event/${eventId}`)}
          >
            Cancel
          </button>
          <button 
            type="button"
            className="mobile-btn mobile-btn-primary"
            onClick={handleSave}
            disabled={saving || !isFormValid}
          >
            {saving ? (
              <>
                <div className="mobile-btn-spinner"></div>
                Saving...
              </>
            ) : (
              <>
                💾 Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MobileEventEditPage;
